import { createSelector, createSlice } from "@reduxjs/toolkit";

import { setDeep } from "Libs/objectAccess";
import logger from "Libs/logger";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";
import { getNewBillingSystemError } from "Hooks/billingSystem/getNewBillingSystemError";

import { getVouchersRequest } from "Api/billingSystem";

import type { RootState } from "Store/configureStore";
import type { Voucher, VoucherList } from "Api/billingSystem/types";

export type VoucherStatus = "completed" | "scheduled" | "expired" | "in_use";

export type VoucherWithStatus = Voucher & {
  remainingBalance: number;
  // null when the status can't be determined (e.g. missing/invalid dates) -> no tag.
  status: VoucherStatus | null;
};

// Balance left on the voucher = its credit minus everything already applied to invoices.
export const getVoucherRemainingBalance = (voucher: Voucher) => {
  const appliedAmount = voucher.invoices.reduce(
    (sum, inv) => sum + parseFloat(inv.invoice_discount),
    0
  );
  return parseFloat(voucher.amount) - appliedAmount;
};

export const getVoucherStatus = (
  voucher: Voucher,
  remainingBalance: number,
  now: Date = new Date()
): VoucherStatus | null => {
  if (remainingBalance <= 0) return "completed"; // fully used, regardless of dates

  const start = new Date(voucher.start_at);
  const end = new Date(voucher.end_at);

  // Scheduled/expired/in_use all depend on the dates; if either is missing or
  // malformed we can't tell them apart, so report no status (renders no tag)
  // rather than letting the active "in_use" state absorb bad data.
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;

  if (now < start) return "scheduled"; // future voucher, balance not yet usable
  if (now > end) return "expired"; // window passed with balance left
  return "in_use"; // active and has balance
};

// Thunks
export const getVouchers = createAppAsyncThunk(
  "billingSystem/vouchers/getByProfile",
  async (
    { billingProfileId }: { billingProfileId: string },
    { rejectWithValue }
  ) => {
    try {
      return await getVouchersRequest(billingProfileId);
    } catch (err) {
      logger(err);
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

// State type
type VouchersState = {
  byProfileId: Record<
    string,
    {
      loading: boolean;
      data?: VoucherList;
      error?: string;
    }
  >;
};

const initialState: VouchersState = {
  byProfileId: {}
};

// Slice
const vouchersSlice = createSlice({
  name: "vouchers",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getVouchers.pending, (state, { meta }) => {
        setDeep(
          state,
          ["byProfileId", meta.arg.billingProfileId, "loading"],
          true
        );
        setDeep(
          state,
          ["byProfileId", meta.arg.billingProfileId, "error"],
          undefined
        );
      })
      .addCase(getVouchers.fulfilled, (state, action) => {
        const { billingProfileId } = action.meta.arg;
        setDeep(state, ["byProfileId", billingProfileId, "loading"], false);
        setDeep(
          state,
          ["byProfileId", billingProfileId, "data"],
          action.payload
        );
      })
      .addCase(getVouchers.rejected, (state, action) => {
        const { billingProfileId } = action.meta.arg;
        setDeep(state, ["byProfileId", billingProfileId, "loading"], false);
        setDeep(
          state,
          ["byProfileId", billingProfileId, "error"],
          action.payload?.error
        );
      });
  }
});

export default vouchersSlice.reducer;

// Selectors
const vouchersStateSelector = (state: RootState) => state.vouchers;

// Vouchers by profile ID
export const selectProfileVouchers = createSelector(
  vouchersStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) => state.byProfileId[billingProfileId]?.data
);

export const selectProfileVouchersLoading = createSelector(
  vouchersStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) =>
    state.byProfileId[billingProfileId]?.loading ?? false
);

export const selectProfileVouchersError = createSelector(
  vouchersStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) => state.byProfileId[billingProfileId]?.error
);

// Individual voucher selectors
export const selectProfileVouchersList = createSelector(
  selectProfileVouchers,
  data => data?.vouchers
);

export const selectProfileVouchersTotal = createSelector(
  selectProfileVouchers,
  data => data?.vouchers_total
);

export const selectProfileVouchersApplied = createSelector(
  selectProfileVouchers,
  data => data?.vouchers_applied
);

export const selectProfileVouchersRemainingBalance = createSelector(
  selectProfileVouchers,
  data => data?.vouchers_remaining_balance
);

export const selectProfileVouchersCurrency = createSelector(
  selectProfileVouchers,
  data => data?.currency
);

// Vouchers enriched with their derived remaining balance and status.
export const selectProfileVouchersWithStatus = createSelector(
  selectProfileVouchersList,
  (vouchers): VoucherWithStatus[] | undefined =>
    vouchers?.map(voucher => {
      const remainingBalance = getVoucherRemainingBalance(voucher);
      return {
        ...voucher,
        remainingBalance,
        status: getVoucherStatus(voucher, remainingBalance)
      };
    })
);
